/*
 * product-reviews.js — PDP review widget.
 *
 * Renders the aggregate header (avg + star row + histogram), a sort
 * control, and a teaser of the most recent reviews for the current
 * product, plus a "View all" link to the store-wide /reviews page
 * filtered to this product. Also populates the lightweight inline rating
 * line next to the product title (single shared aggregate fetch).
 *
 * Endpoints (anonymous App Proxy — HMAC added by Shopify, no tokens):
 *   aggregate : GET  /apps/dashboard/reviews/aggregate?product=<gid>
 *               → { avg_rating, total_reviews, distribution:{'1'..'5'} }
 *   list      : GET  /apps/dashboard/reviews?product=<gid>&sort=&page=
 *               → { reviews:[...], page, page_size, total }
 *   vote      : POST /apps/dashboard/reviews/<id>/helpful
 *               → { ok, already_voted }
 *
 * Product identity is the GID (gid://shopify/Product/N) — the format
 * reviews.shopify_product_id stores. The GID contains slashes, so it rides
 * in the QUERY STRING (encoded '%2F' is NOT preserved in a path segment
 * through the App Proxy — that path 404s). The Liquid emits it as
 * data-product-gid; we encodeURIComponent it into the query param.
 *
 * States (find-a-store discipline — never a blank/broken region):
 *   loading → transient hint
 *   empty   → "No reviews yet — be the first" (the LAUNCH DEFAULT; reads
 *             intentional, not broken)
 *   error   → unobtrusive line, the rest of the PDP is untouched
 *   content → summary + sort + cards + view-all
 *
 * XSS: review body/title/name/location are user-submitted; every
 * interpolated string is escapeHtml()'d before it touches innerHTML.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-product-reviews]');
  if (!root) return;
  var gid = root.getAttribute('data-product-gid');
  if (!gid) return;

  var encodedGid = encodeURIComponent(gid);
  // GID rides in the query string (proxy-safe), never a path segment.
  var AGG_URL = '/apps/dashboard/reviews/aggregate?product=' + encodedGid;
  var LIST_URL = '/apps/dashboard/reviews?product=' + encodedGid;
  var REVIEWS_PAGE = '/pages/reviews?product=' + encodedGid;
  var TEASER = 5; // show ~5 on the PDP, then link to the full /reviews page

  function $(sel) { return root.querySelector(sel); }

  var loadingEl = $('[data-pr-loading]');
  var contentEl = $('[data-pr-content]');
  var emptyEl   = $('[data-pr-empty]');
  var errorEl   = $('[data-pr-error]');
  var avgEl     = $('[data-pr-avg]');
  var avgStarsEl = $('[data-pr-avg-stars]');
  var countEl   = $('[data-pr-count]');
  var histEl    = $('[data-pr-histogram]');
  var sortEl    = $('[data-pr-sort]');
  var gridEl    = $('[data-pr-grid]');
  var viewAllEl = $('[data-pr-viewall]');
  // Inline rating line lives in the product title block (a separate
  // section), so query the whole document — populated from the same fetch.
  var inlineEl  = document.querySelector('[data-pr-inline]');

  var state = { sort: 'recent', total: 0 };

  // ─── Escape helper (XSS-safe text insertion) ─────────────────────
  function escapeHtml(s) {
    if (s === null || s === undefined) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  // Filled ★ for the rating, hollow ☆ for the remainder (1-5).
  function starGlyphs(rating) {
    var n = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    var out = '';
    for (var i = 0; i < 5; i++) out += i < n ? '★' : '☆';
    return out;
  }

  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function fmtDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate() + ', ' + d.getUTCFullYear();
  }

  // ─── State machine (toggle the four regions) ─────────────────────
  function showState(name) {
    if (loadingEl) loadingEl.hidden = name !== 'loading';
    if (contentEl) contentEl.hidden = name !== 'content';
    if (emptyEl)   emptyEl.hidden   = name !== 'empty';
    if (errorEl)   errorEl.hidden   = name !== 'error';
  }

  // ─── Render: aggregate summary (avg + stars + histogram) ─────────
  function renderSummary(agg) {
    var avg = Number(agg.avg_rating) || 0;
    var total = Number(agg.total_reviews) || 0;
    if (avgEl) avgEl.textContent = avg.toFixed(1);
    if (avgStarsEl) avgStarsEl.textContent = starGlyphs(avg);
    if (countEl) countEl.textContent = total + (total === 1 ? ' review' : ' reviews');

    if (histEl) {
      // CONTRACT: aggregate.distribution is keyed '1'..'5'; each bar is
      // distribution[n] / total. (There are NO star_N_count fields.)
      var dist = agg.distribution || {};
      var rows = '';
      for (var star = 5; star >= 1; star--) {
        var cnt = Number(dist[String(star)]) || 0;
        var pct = total > 0 ? Math.round((cnt / total) * 100) : 0;
        rows +=
          '<div class="pr-hist-row">' +
            '<span class="pr-hist-label">' + star + '★</span>' +
            '<span class="pr-hist-bar"><span class="pr-hist-fill" style="width:' + pct + '%;"></span></span>' +
            '<span class="pr-hist-count">' + cnt + '</span>' +
          '</div>';
      }
      histEl.innerHTML = rows;
    }
  }

  // ─── Render: inline rating line by the product title ─────────────
  function renderInline(agg) {
    if (!inlineEl) return;
    var avg = Number(agg.avg_rating) || 0;
    var total = Number(agg.total_reviews) || 0;
    if (total <= 0) return; // 0 reviews → leave it hidden (no clutter)
    inlineEl.innerHTML =
      '<span class="pr-inline-stars" aria-hidden="true">' + starGlyphs(avg) + '</span>' +
      '<span class="pr-inline-text">' + avg.toFixed(1) + ' · ' + total + (total === 1 ? ' review' : ' reviews') + '</span>';
    inlineEl.hidden = false;
  }

  // Media strip: thumbnails that open the lightbox. The url is our derived
  // public-bucket URL (trusted) — escapeHtml'd in the attribute defensively,
  // and the lightbox reads it from the data attribute (set as .src property).
  function mediaStripHtml(media) {
    if (!Array.isArray(media) || media.length === 0) return '';
    return '<div class="pr-card-media">' + media.map(function (m) {
      var u = escapeHtml(m.url);
      if (m.media_type === 'video') {
        return '<button type="button" class="pr-media-thumb is-video" data-pr-lightbox data-media-type="video" data-media-url="' + u + '" aria-label="Play video">▶</button>';
      }
      return '<button type="button" class="pr-media-thumb" data-pr-lightbox data-media-type="image" data-media-url="' + u + '" aria-label="View photo"><img src="' + u + '" alt="" loading="lazy"></button>';
    }).join('') + '</div>';
  }

  // Admin response — "Response from Circadia" sub-block, shown only when the
  // review carries one (projection: r.response = { response_body, responded_at }).
  // response_body is admin-entered copy → escapeHtml'd like all other strings.
  function responseHtml(r) {
    if (!r.response || !r.response.response_body) return '';
    return (
      '<div class="pr-response">' +
        '<div class="pr-response-label">Response from Circadia</div>' +
        '<p class="pr-response-body">' + escapeHtml(r.response.response_body) + '</p>' +
        '<div class="pr-response-date">' + escapeHtml(fmtDate(r.response.responded_at)) + '</div>' +
      '</div>'
    );
  }

  // ─── Render: one review card (reuses the .review-card vocabulary) ─
  function cardHtml(r) {
    var titleHtml = r.title
      ? '<h4 class="pr-review-title">' + escapeHtml(r.title) + '</h4>'
      : '';
    var locHtml = r.reviewer_location
      ? '<div class="review-loc">' + escapeHtml(r.reviewer_location) + '</div>'
      : '';
    var verifiedHtml = r.verified_purchase
      ? '<span class="pr-verified">✓ Verified purchase</span>'
      : '';
    var editedHtml = r.edited ? ' <span class="pr-edited">(edited)</span>' : '';
    var count = Number(r.helpful_count) || 0;
    return (
      '<article class="review-card pr-card">' +
        '<div class="pr-card-top">' +
          '<div class="review-stars" aria-hidden="true">' + starGlyphs(r.rating) + '</div>' +
          '<span class="pr-date">' + escapeHtml(fmtDate(r.created_at)) + editedHtml + '</span>' +
        '</div>' +
        '<div class="pr-card-body">' +
          titleHtml +
          '<p class="review-quote">“' + escapeHtml(r.body) + '”</p>' +
        '</div>' +
        mediaStripHtml(r.media) +
        responseHtml(r) +
        '<div class="review-foot">' +
          '<div>' +
            '<div class="review-author">' + escapeHtml(r.reviewer_name) + '</div>' +
            locHtml +
            verifiedHtml +
          '</div>' +
          '<button type="button" class="pr-helpful" data-helpful data-review-id="' + escapeHtml(r.id) +
            '" data-base-count="' + count + '" data-voted="false">' +
            'Helpful (' + count + ')' +
          '</button>' +
        '</div>' +
      '</article>'
    );
  }

  function renderList(list) {
    if (!gridEl) return;
    var shown = list.slice(0, TEASER);
    gridEl.innerHTML = shown.map(cardHtml).join('');
    if (viewAllEl) {
      if (state.total > shown.length) {
        viewAllEl.textContent = 'View all ' + state.total + ' reviews →';
        viewAllEl.href = REVIEWS_PAGE;
        viewAllEl.hidden = false;
      } else {
        viewAllEl.hidden = true;
      }
    }
  }

  // ─── Helpful vote (optimistic, dedup-safe, no double-count) ──────
  function setHelpfulLabel(btn, n) {
    btn.textContent = 'Helpful (' + n + ')';
  }

  function onVote(btn) {
    // Double-submit guard: in-flight (disabled) or already voted → ignore.
    if (btn.disabled || btn.getAttribute('data-voted') === 'true') return;
    var id = btn.getAttribute('data-review-id');
    if (!id) return;
    var base = Number(btn.getAttribute('data-base-count')) || 0;

    // Optimistic: show base+1 immediately, lock the button, mark voted.
    btn.disabled = true;
    btn.setAttribute('data-voted', 'true');
    setHelpfulLabel(btn, base + 1);

    fetch('/apps/dashboard/reviews/' + encodeURIComponent(id) + '/helpful', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (body) {
        if (body && body.already_voted === true) {
          // Server didn't add (this IP already voted) → roll the optimistic
          // increment back so we never over-count. Button stays locked.
          setHelpfulLabel(btn, base);
        }
        // already_voted === false → the +1 was real; keep it, stay locked.
      })
      .catch(function () {
        // Failed → revert the optimistic increment and let them retry.
        setHelpfulLabel(btn, base);
        btn.setAttribute('data-voted', 'false');
        btn.disabled = false;
      });
  }

  if (gridEl) {
    gridEl.addEventListener('click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-helpful]') : null;
      if (btn && gridEl.contains(btn)) onVote(btn);
      var media = e.target.closest ? e.target.closest('[data-pr-lightbox]') : null;
      if (media && gridEl.contains(media)) {
        openLightbox(media.getAttribute('data-media-url'), media.getAttribute('data-media-type'));
      }
    });
  }

  // ─── Lightbox (one shared overlay; opens full image / playable video) ──
  var lightboxEl = null;
  function closeLightbox() {
    if (!lightboxEl) return;
    lightboxEl.hidden = true;
    lightboxEl.innerHTML = ''; // stops any playing video
  }
  function openLightbox(mediaUrl, type) {
    if (!mediaUrl) return;
    if (!lightboxEl) {
      lightboxEl = document.createElement('div');
      lightboxEl.className = 'pr-lightbox';
      lightboxEl.hidden = true;
      lightboxEl.addEventListener('click', closeLightbox); // backdrop closes
      document.body.appendChild(lightboxEl);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeLightbox();
      });
    }
    lightboxEl.innerHTML = '';
    var el;
    if (type === 'video') {
      el = document.createElement('video');
      el.controls = true;
      el.autoplay = true;
      el.playsInline = true;
    } else {
      el = document.createElement('img');
      el.alt = '';
    }
    el.className = 'pr-lightbox-media';
    el.src = mediaUrl; // .src property — never innerHTML
    el.addEventListener('click', function (e) { e.stopPropagation(); }); // media click ≠ close
    lightboxEl.appendChild(el);
    lightboxEl.hidden = false;
  }

  // ─── Fetch: review list for the current sort ─────────────────────
  // Reuses the store-wide list (proxy-safe ?product=) scoped to this
  // product; envelope is { reviews, page, page_size, total } — the widget
  // only reads `reviews` here (the count/threshold comes from the aggregate).
  function fetchList() {
    return fetch(LIST_URL + '&sort=' + encodeURIComponent(state.sort) + '&page=1', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (body) {
        return (body && Array.isArray(body.reviews)) ? body.reviews : [];
      });
  }

  if (sortEl) {
    sortEl.addEventListener('change', function () {
      state.sort = sortEl.value || 'recent';
      fetchList()
        .then(renderList)
        // Sort re-fetch failure: keep the existing list rather than nuking
        // the whole widget into an error state.
        .catch(function (err) { console.error('[product-reviews] sort failed:', err); });
    });
  }

  // ─── Init: aggregate first (drives empty vs content), then list ──
  function init() {
    showState('loading');
    fetch(AGG_URL, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (agg) {
        state.total = Number(agg.total_reviews) || 0;
        if (state.total === 0) {
          showState('empty'); // the launch default — intentional, not broken
          return;
        }
        renderSummary(agg);
        renderInline(agg);
        return fetchList().then(function (list) {
          renderList(list);
          showState('content');
        });
      })
      .catch(function (err) {
        showState('error');
        console.error('[product-reviews] load failed:', err);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
